import {
  timeInMinsSecs,
  formatTime,
  getDateShort,
  getTimeFormatInShortFormWithoutSecs,
  getYear,
  compareDates,
  addTime
} from '@utils/dates';
import { EVALUATION_STATUS } from '@constants/quiz';
import { sessionStates } from '@constants/index';
import { Spacings } from '@styles/index';
import isEmpty from '@utils/isEmpty';

const learnerWaitTimeForClassRecording = 3 * 3600 * 1000; // 3 hours
export const classCancelledMsg = 'Class was cancelled by the Educator';

const getLatestSession = (session) => ({
  correctCount: session.correct_count,
  incorrectCount: session.incorrect_count,
  finishedAt: session.finished_at ? new Date(session.finished_at) : null,
  isLive: session.is_live,
  score: session.score,
  skippedCount: session.skipped_count,
  startTime: session.start_time,
  uid: session.uid
});

const slides = (pdf) => ({
  withAnnotations: pdf.with_annotation,
  withoutAnnotations: pdf.no_annotation
});

export const isPost = (data) => {
  return data?.type === 'post';
};

export const isQuiz = (data) => {
  return data?.type === 'quiz';
};

const getNow = () => {
  return new Date().getTime();
};

export const getState = (data) => data?.state || sessionStates.FUTURE;

export const getLiveAt = (data) =>
  data?.liveAt ? new Date(data.liveAt).getTime() : 0;

export const getTimeDifference = (data) => {
  const liveAt = getLiveAt(data);
  const now = getNow();
  return (liveAt - now) / 1000;
};

export const isRecorded = (data) => getState(data) === sessionStates.PAST;

export const classDidNotHappen = (
  {
    secondsBeforeLive = 0,
    totalQuestions = 0,
    finishedAt = 0,
    liveAt,
    state,
    type
  },
  timeLimit = learnerWaitTimeForClassRecording
) => {
  const liveAtTime = getLiveAt({ liveAt });
  const lessonState = getState({ state });
  const now = getNow();
  const waitTime = new Date(finishedAt).getTime() + timeLimit;
  const isLesson = isPost({ type });
  if (
    isLesson &&
    lessonState === sessionStates.FUTURE &&
    secondsBeforeLive === 0 &&
    now > waitTime
  )
    return true;
  if (!isLesson && now > liveAtTime && totalQuestions === 0) return true;
  return false;
};

// type should always be post
export const isCurrentlyLive = (data, skipTimeCheck) => {
  const state = getState(data);
  const diff = getTimeDifference(data);
  const isClassCancelled = classDidNotHappen(data);
  if (
    state === sessionStates.LIVE ||
    (state === sessionStates.FUTURE &&
      (!isClassCancelled || skipTimeCheck) &&
      (skipTimeCheck || data.secondsBeforeLive <= 900 || diff <= 900))
  )
    return true;
  return false;
};

export const isUpcoming = (data) => {
  if (isPost(data)) {
    return (
      !classDidNotHappen(data) && !isRecorded(data) && !isCurrentlyLive(data)
    );
  }
  // for type quiz
  const diff = getTimeDifference(data);
  return diff > 0;
};

export const isPostClickable = (data) => !isUpcoming(data);

export const isCompletedWithNoVideoUrl = (data) => {
  return isPost(data) && isRecorded(data) && !data.videoUrl;
};

export const hasPDF = (data) => {
  return isRecorded(data) && data.slides;
};

export const getVideoUrlInternal = (data, isExternal) => {
  if (isCurrentlyLive(data, isExternal)) return data.liveClassUrl;
  if (isRecorded(data)) {
    return data.videoUrl || data.liveClassUrl;
  }
  return null;
};

export const getVideoUrl = (unLiveUrl, data, isExternal) => {
  const videoUrl = getVideoUrlInternal(data, isExternal);
  if (unLiveUrl === '') return videoUrl;
  if (
    videoUrl &&
    unLiveUrl &&
    unLiveUrl.split('/v')[1]?.split('/')[0] !==
      videoUrl.split('/v')[1]?.split('/')[0]
  )
    return videoUrl;
  return unLiveUrl;
};

export const isClassMissedByEducator = (data) => {
  let liveAt = getLiveAt(data);
  liveAt += data.lessonDuration * 60000;
  const currentDate = new Date().getTime();
  return (
    data.type === 'post' &&
    data.state === sessionStates.FUTURE &&
    liveAt < currentDate
  );
};

export const getLessonInfoString = (
  lesson,
  forPlus,
  isBatch,
  isEducatorSide = false,
  showLessonRank = true
) => {
  if (!forPlus) return timeInMinsSecs(lesson.lessonDuration);
  const timeLimit = isEducatorSide
    ? 3600 * 1000
    : learnerWaitTimeForClassRecording;
  if (classDidNotHappen(lesson, timeLimit)) return classCancelledMsg;
  if (!lesson.liveAt) {
    return '';
  }
  let infoString = showLessonRank
    ? `Lesson ${lesson.contentRank}${Spacings.SPACING_2SPACES}•${Spacings.SPACING_2SPACES}`
    : '';
  if (lesson.isLive) {
    infoString += `Started at ${formatTime(lesson.startedAt || lesson.liveAt)}`;
    if (isBatch)
      return `${infoString}${Spacings.SPACING_2SPACES}•${Spacings.SPACING_2SPACES}${lesson.author.name}`;
    return infoString;
  }
  if (lesson.state === sessionStates.PAST) {
    infoString += `${getDateShort(lesson.liveAt)}${Spacings.SPACING_2SPACES}•${
      Spacings.SPACING_2SPACES
    }${getTimeFormatInShortFormWithoutSecs(lesson.lessonDuration)}`;
  } else {
    infoString += `${formatTime(lesson.liveAt)} `;
  }
  if (isBatch)
    return `${infoString}${Spacings.SPACING_2SPACES}•${Spacings.SPACING_2SPACES}${lesson.author.name}`;
  return infoString;
};

export const getSpecialInfoString = (lesson) => {
  if (lesson.state === sessionStates.PAST)
    return `${getDateShort(lesson.liveAt)}, ${getYear(
      lesson.liveAt
    )} • ${getTimeFormatInShortFormWithoutSecs(lesson.lessonDuration)}`;
  if (lesson.state === sessionStates.LIVE)
    return `Started on ${formatTime(lesson.liveAt)}`;
  return `Starts on ${getDateShort(lesson.liveAt)}, ${getYear(
    lesson.liveAt
  )} • ${formatTime(lesson.liveAt)}`;
};

export const quizState = (lesson) => {
  const currentDate = new Date();
  if (
    new Date(lesson.liveAtString).getTime() <= currentDate.getTime() &&
    new Date(lesson.endsAt).getTime() >= currentDate.getTime()
  )
    return sessionStates.LIVE;
  const val = compareDates(lesson.startsAt, currentDate);
  if (val === -1) return sessionStates.FUTURE;
  if (val === 1) return sessionStates.PAST;
};

export const checkForEvaluationPending = (evaluationState) => {
  switch (evaluationState) {
    case EVALUATION_STATUS.COMPLETED:
      return false;

    default:
      return true;
  }
};

export const getQuizInfoString = (
  quiz,
  isTestSeries,
  isEducatorSide = false,
  showQuizNumber = true
) => {
  const { startsAt, contentRank, latestSession, totalScore } = quiz;
  if (!startsAt) {
    return '';
  }
  if (classDidNotHappen(quiz))
    return `${
      isTestSeries ? 'Test' : 'Quiz'
    } was cancelled/rescheduled by the Educator`;

  if (
    quiz?.latest_session?.evaluation_state !== undefined &&
    checkForEvaluationPending(quiz?.latest_session?.evaluation_state)
  ) {
    return 'You will be notified as soon the result is available';
  }

  let infoString = showQuizNumber
    ? `${isTestSeries ? 'Test' : 'Quiz'} ${contentRank}${
        Spacings.SPACING_2SPACES
      }•${Spacings.SPACING_2SPACES}`
    : '';

  if (!isEducatorSide && latestSession) {
    if (!isEmpty(latestSession.score)) {
      infoString += `${latestSession.score}/${totalScore} marks${Spacings.SPACING_2SPACES}•${Spacings.SPACING_2SPACES}`;
    }

    infoString += `${getDateShort(quiz.liveAtString)}`;

    return infoString;
  }

  if (
    !isEducatorSide &&
    !latestSession &&
    quizState(quiz) === sessionStates.PAST
  ) {
    infoString += `Missed${Spacings.SPACING_2SPACES}•${
      Spacings.SPACING_2SPACES
    }${getDateShort(quiz.liveAtString)}`;
    return infoString;
  }

  infoString += `${formatTime(startsAt)}`;

  return infoString;
};

class Item {
  constructor(itemdata) {
    this.week = itemdata.week;
    this.type = itemdata.type ?? 'post';
    if (this.type === 'live_class' || this.type === 'LiveClass') {
      this.type = 'post';
    }
    if (this.type === 'Test' || this.type === 'Quiz') {
      this.type = 'quiz';
    }
    this.rank = itemdata.rank;
    this.contentRank = itemdata.content_type_item_rank;
    this.courseUID = itemdata.courseUID;
    this.courseSlug = itemdata.courseSlug;
    this.topicGroups = itemdata.topic_groups
      ? itemdata.topic_groups.map((topicGroup) => {
          return { topology: topicGroup };
        })
      : [];
    const item = itemdata.properties ? itemdata.properties : itemdata;

    this.uid = item.uid;
    this.name = item.name || item.title;
    this.description = item.description || null;
    this.permalink = item.permalink;
    this.relativeLink = item.relative_link;
    this.createdAt = item.created_at;
    this.slug = item.slug;
    this.language = item.language;
    this.thumbnail = item.thumbnail || item.video?.thumbnail;
    this.isSpecial = item.is_special;
    this.classType = item.class_type;
    this.collectionName = item.collection_name;
    this.collection = item.collection;
    this.liveAt = item.live_at
      ? new Date(item.live_at)
      : new Date(item.start_time);
    this.liveAtString = item.live_at || item.start_time;
    this.startedAt = item.started_at ? new Date(item.started_at) : null;
    this.author = item.author || null;
    this.startsAt = item.start_time || null;
    this.goalUid = item.goal_uid || null;
    this.programmeUid =
      item.programmeUid || item.programme?.uid || item.collection?.uid;
    if (item.programme || item.collection) {
      this.programme = item.programme
        ? { course: item.programme }
        : { course: item.collection };
    }
    if (!this.contentRank)
      this.contentRank = item.content_type_item_rank || item.rank;
    this.hasWatched = item.has_watched || item.watched || item.has_attempted;
    this.liveClassUrl = item.live_class_url;
    this.videoUrl = item.video_url || item.video?.url;
    this.lessonDuration =
      item.video_duration || item.video?.duration || item.duration;
    this.endsAt = addTime(this.liveAt, this.lessonDuration, 's');
    this.finishedAt = item.finished_at
      ? new Date(item.finished_at)
      : this.endsAt;
    this.embedUrl = item.video?.embed_url;
    this.cdnUrl = item.video?.cdn_url;
    this.videoType = item.video?.type;
    this.youtubeId = item.video?.youtube_id;
    this.key = item.distribution_key ? item.distribution_key : item.key || null;
    this.isPdfAvailable = item.video?.is_pdf_available;
    this.pdfUrl = item.video?.pdf_url;
    this.secondsBeforeLive = item.seconds_before_live;
    this.state = this.type === 'post' ? item.state : quizState(this);
    this.isLive =
      item.is_live || this.type === 'post'
        ? item.state === sessionStates.LIVE
        : this.secondsBeforeLive === 0 &&
          compareDates(new Date(), this.startsAt) === 0;
    if (this.type === 'quiz')
      this.isLive = quizState(this) === sessionStates.LIVE;
    this.watchTime = item.watch_time;
    this.slides = item.slides_pdf ? slides(item.slides_pdf) : null;
    this.latestSession = item.latest_session
      ? getLatestSession(item.latest_session)
      : null;
    this.totalQuestions = item.num_questions;
    this.totalScore = item.total_score;
    this.upvotes = item.recommend_count;
    this.goal = { topology: item.goal };
    this.specialInfoString =
      this.type === 'post' || this.classType === 1
        ? getSpecialInfoString(this)
        : '';
    this.isBookmarked = item.is_bookmarked || item.bookmarked || false;
    this.ogImage = item.og_image || null;
    this.programmeNM = item.collection2;
    this.liveLearnersCount = item.live_learners_count;
    this.totalWatchTime = item.total_watch_time;
    this.likePercentage = item.like_percentage;
    this.topology = item.topology ? { topology: item.topology } : null;
    // Tags value map
    // NA = 0  # Not Available
    // PANEL_DISCUSSION = 1
    // MUST_WATCH = 2
    this.tags = item.tags ?? [];
    this.isSaved = item.is_saved;
    this.contentState = item.content_state || null;
    // adding this bcoz using programme key is creating a loop (sometimes) while denormalizing
    // due to next_session key - happens when lesson/test in next_session is saved.
    this.course = item.course;
  }
}

const getEntityData = (data) => {
  const item = new Item(data);
  return item;
};

export const checkIfVersionMismatches = (unLiveUrl, data) => {
  const videoUrl = getVideoUrlInternal(data);
  if (
    videoUrl &&
    unLiveUrl &&
    unLiveUrl.split('/v')[1]?.split('/')[0] !==
      videoUrl.split('/v')[1]?.split('/')[0]
  )
    return true;
  return false;
};

const getLink = (data) => {
  const { nextTrack } = data ?? {};
  if (!nextTrack) return '';
  const { slug, uid } = nextTrack;
  if (slug && uid) return `/class/${slug}/${uid}`;
  const { relativeLink } = data;
  return relativeLink ?? '';
};

const getLinkPath = () => '/class/[slug]/[uid]';

export const getInfoString = (
  lesson,
  isTestSeries,
  forPlus,
  isBatch,
  isEducatorSide = false,
  showLessonRank = true,
  showQuizNumber = true
) => {
  if (lesson.type === 'post')
    return getLessonInfoString(
      lesson,
      forPlus,
      isBatch,
      isEducatorSide,
      showLessonRank
    );
  if (lesson.type === 'quiz')
    return getQuizInfoString(
      lesson,
      isTestSeries,
      isEducatorSide,
      showQuizNumber
    );
};

export const getAsAndHrefOfLesson = (lesson) => {
  const { slug: lessonSlug, uid: lessonUID } = lesson;
  const asLink = `/lesson/${lessonSlug}/${lessonUID}`;
  const hrefLink = '/lesson/[lessonSlug]/[lessonUID]';

  return { asLink, hrefLink };
};

export const getItemRedirectURL = (lesson, forPlus) => {
  const { type, slug, uid } = lesson;
  if (!forPlus)
    return {
      href: '/lesson/[lessonSlug]/[lessonUID]',
      as: `/lesson/${slug}/${uid}`
    };
  if (type === 'post')
    return { href: '/class/[slug]/[uid]', as: `/class/${slug}/${uid}` };
  if (type === 'quiz')
    return { href: '/quiz/[quizSlug]/[quizUID]', as: `/quiz/${slug}/${uid}` };
  return { href: '/class/[slug]/[uid]', as: `/class/${slug}/${uid}` };
};

export const isLessonLive = (lesson, timeLimit = 0) => {
  if (!classDidNotHappen(lesson, timeLimit) && isCurrentlyLive(lesson))
    return true;
  return false;
};

export default {
  Item,
  getEntityData,
  isUpcoming,
  isClassMissedByEducator,
  hasPDF,
  getVideoUrl,
  getLink,
  getLinkPath,
  getInfoString,
  getAsAndHrefOfLesson,
  isPostClickable,
  getSpecialInfoString,
  getItemRedirectURL,
  checkIfVersionMismatches,
  isCompletedWithNoVideoUrl,
  getQuizInfoString,
  isLessonLive,
  classDidNotHappen,
  isCurrentlyLive,
  isPost,
  isRecorded,
  getTimeDifference,
  isQuiz
};
